import { toPng } from "html-to-image";
import {
  Camera,
  FileAudio,
  FileX,
  HelpCircle,
  History,
  PanelLeftClose,
  PanelLeftOpen,
  Play,
  Share2,
  UploadCloud,
} from "lucide-react";
import { type ComponentType, useEffect, useState } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useArchViz } from "./ArchVizContext";
import { ViewSettingsPopover } from "./ViewSettingsPopover";

interface LeftRailProps {
  readOnly?: boolean;
}

const RAIL_EXPANDED_KEY = "archviz:rail-expanded";

function readRailExpanded(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(RAIL_EXPANDED_KEY) === "true";
}

function RailButton({
  icon: Icon,
  label,
  onClick,
  badge,
  danger,
  expanded,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  badge?: number;
  danger?: boolean;
  expanded: boolean;
}) {
  return (
    <button
      type="button"
      title={expanded ? undefined : label}
      aria-label={label}
      onClick={onClick}
      className={cn(
        "relative h-9 rounded-lg flex items-center transition-colors",
        expanded ? "w-full justify-start gap-2 px-2" : "w-9 justify-center",
        danger
          ? "text-destructive hover:bg-destructive/10"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      <Icon className="h-[18px] w-[18px] shrink-0" />
      {expanded && <span className="text-sm truncate">{label}</span>}
      {badge !== undefined && badge > 0 && (
        <span
          className={cn(
            "min-w-3.5 h-3.5 px-1 rounded-full bg-brand-600 text-[9px] font-medium text-white flex items-center justify-center",
            expanded ? "ml-auto" : "absolute -top-0.5 -right-0.5",
          )}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

function RailDivider({ expanded }: { expanded: boolean }) {
  return (
    <span className={cn("my-1 h-px bg-border", expanded ? "w-full" : "w-6")} aria-hidden="true" />
  );
}

export function LeftRail({ readOnly }: LeftRailProps = {}) {
  const { state, dispatch, extras } = useArchViz();
  const arch = state.architecture;
  const [expanded, setExpanded] = useState(readRailExpanded);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(RAIL_EXPANDED_KEY, String(expanded));
  }, [expanded]);

  const { solutionSlug, canWrite, onUpload, onShare, onMedia } = extras;
  if (!arch) return null;

  const flowCount = arch.dataFlows.length;
  const questionCount = arch.questions?.length ?? 0;

  const exportPng = async () => {
    const viewport = document.querySelector(".react-flow__viewport") as HTMLElement | null;
    if (!viewport) {
      toast.error("Canvas not ready");
      return;
    }
    try {
      const bg = getComputedStyle(document.body).backgroundColor || "#ffffff";
      const dataUrl = await toPng(viewport, { backgroundColor: bg, pixelRatio: 2 });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `${arch.meta.name.replace(/\s+/g, "-")}-L${state.drillLevel}.png`;
      link.click();
      toast.success("Exported as PNG");
    } catch (err) {
      console.error("[archviz] PNG export failed", err);
      toast.error("Could not export PNG");
    }
  };

  const hasSolutionActions = Boolean(solutionSlug || onMedia || (canWrite && onUpload) || onShare);

  return (
    <aside
      aria-label="Toolbar"
      className={cn(
        "shrink-0 border-r border-border bg-card/60 flex flex-col gap-0.5 py-2 transition-[width] duration-base ease-out-soft",
        expanded ? "w-44 items-stretch px-2" : "w-12 items-center",
      )}
    >
      <button
        type="button"
        title={expanded ? "Collapse" : "Expand"}
        aria-label={expanded ? "Collapse toolbar" : "Expand toolbar"}
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        className={cn(
          "h-9 rounded-lg flex items-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors",
          expanded ? "w-full justify-start gap-2 px-2" : "w-9 justify-center",
        )}
      >
        {expanded ? (
          <PanelLeftClose className="h-[18px] w-[18px] shrink-0" />
        ) : (
          <PanelLeftOpen className="h-[18px] w-[18px] shrink-0" />
        )}
        {expanded && <span className="text-sm truncate">Collapse</span>}
      </button>

      <RailDivider expanded={expanded} />
      <ViewSettingsPopover expanded={expanded} />

      {(flowCount > 0 || questionCount > 0) && <RailDivider expanded={expanded} />}
      {flowCount > 0 && (
        <RailButton
          icon={Play}
          label="Flows"
          badge={flowCount}
          expanded={expanded}
          onClick={() => dispatch({ type: "OPEN_DIALOG", dialog: "flows" })}
        />
      )}
      {questionCount > 0 && (
        <RailButton
          icon={HelpCircle}
          label="Questions"
          badge={questionCount}
          expanded={expanded}
          onClick={() => dispatch({ type: "OPEN_DIALOG", dialog: "questions" })}
        />
      )}

      {hasSolutionActions && <RailDivider expanded={expanded} />}
      {solutionSlug && (
        <RailButton
          icon={History}
          label="Versions"
          expanded={expanded}
          onClick={() => dispatch({ type: "OPEN_DIALOG", dialog: "versions" })}
        />
      )}
      {onMedia && (
        <RailButton icon={FileAudio} label="Media" expanded={expanded} onClick={onMedia} />
      )}
      {canWrite && onUpload && (
        <RailButton icon={UploadCloud} label="New version" expanded={expanded} onClick={onUpload} />
      )}
      {onShare && <RailButton icon={Share2} label="Share" expanded={expanded} onClick={onShare} />}

      <RailDivider expanded={expanded} />
      <RailButton
        icon={Camera}
        label="Export PNG"
        expanded={expanded}
        onClick={() => void exportPng()}
      />

      {!readOnly && (
        <>
          <span className="flex-1" />
          <RailButton
            icon={FileX}
            label="Close file"
            danger
            expanded={expanded}
            onClick={() => dispatch({ type: "CLEAR" })}
          />
        </>
      )}
    </aside>
  );
}
